/**
 * FundraiserDetails.tsx - Fundraiser Details and Management Interface
 *
 * This component provides a comprehensive details view for individual fundraisers
 * with tabbed navigation for different aspects of fundraiser management including
 * activity, promoters, donors, and donations.
 *
 * Key Features:
 * - Tabbed interface (Activity, Promoters, Donors, Donations)
 * - Fundraiser header with key metrics and actions
 * - Edit actions dropdown with management options
 * - Modal integrations for sharing, copying, and embedding
 * - Status management and link updates
 * - Delete functionality with confirmation
 * - Responsive design with mobile navigation
 *
 * Props: None (uses MobX store for selected fundraiser)
 *
 * Dependencies: MobX store, tab components, modal components, action dropdowns
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft, X, Heart } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { FundraiserActivityTab } from "./fundraiser-details/FundraiserActivityTab";
import FundraiserPromotersTab from "./fundraiser-details/FundraiserPromotersTab";
import { FundraiserDonorsTab } from "./fundraiser-details/FundraiserDonorsTab";
import { FundraiserDonationsTab } from "./fundraiser-details/FundraiserDonationsTab";
import { EditActionsDropdown } from "./EditActionsDropdown";
import { SocialShareModal } from "./SocialShareModal";
import CopyFundraiserModal from "./CopyFundraiserModal";
import { Toast } from "./Toast";
import { UpdateLinkModal } from "./UpdateLinkModal";
import { UpdateStatusModal } from "./UpdateStatusModal";
import { EmbedFundraiserModal } from "./EmbedFundraiserModal";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { Fundraiser } from "../models/FundraisersGridModels";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { SubscriptionModel } from "../models/SubscriptionModels";
import { FundRaiserStatus, RedirectOtherFundraiserType } from "../enums";
import {
  CreateFundraiserModel,
  FundraisersDonationsBoardResponse,
  PromoterProgressModel,
} from "../models";
import { NavigateFunction } from "react-router-dom";
import ToastService from "./ToastService";
import { canManageFundraiser } from "../utils/permissions";

const DetailsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 25%,
    #f093fb 50%,
    #f5576c 75%,
    #4facfe 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(props) =>
    props.isScrolled
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      : "transparent"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(20px)" : "none")};
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid rgba(255, 255, 255, 0.3)" : "none"};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "32px 0")};
  transition: all 0.3s ease;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  padding-bottom: 80px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const CenterSection = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "active":
        return "background: #dcfce7; color: #166534;";
      case "completed":
        return "background: #dbeafe; color: #1e40af;";
      case "inactive":
        return "background: #fef3c7; color: #92400e;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const HeaderButton = styled.button<{ variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  min-width: 0;
  flex-shrink: 0;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  /* Ensure icon doesn't shrink */
  img {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const TabWrapper = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  border-radius: 16px;
  padding: 6px;
  display: flex;
  gap: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.active
      ? ` /* Changed hover color for unselected segments */
    background: #152837;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(21, 40, 55, 0.3);
    transform: translateY(-1px);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      background: rgba(21, 40, 55, 0.08);
      color: #152837;
    }
  `}
`;

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  margin: 22px 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 32px;
  margin-top: 0;
`;

const DeleteLink = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #b91c1c;
    text-decoration: underline;
  }

  &:active {
    color: #991b1b;
  }
`;

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div<{ hasWarning?: boolean }>`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  max-height: ${(props) => (props.hasWarning ? "650px" : "550px")};
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
`;

const WarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  color: #dc2626;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: normal;
  color: #374151;
  margin-bottom: 8px;

  strong {
    color: #dc2626;
  }
`;

const DeleteConfirmInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background-color: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
`;

const URLInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const DomainSelect = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #f8fafc;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const NameInputContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
`;

const NameInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const URLPreview = styled.div<{ hasError?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  padding: 8px 0;
  margin-top: 10px;
  font-size: 12px;
  text-align: left;
  margin-bottom: 20px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  color: ${(props) => (props.hasError ? "#dc2626" : "#64748b")};
  word-break: break-all;
  z-index: 10;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const YellowWarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #dc2626;
  border-radius: 12px;
  padding: 16px;
  margin: 60px 0 32px 0;
  color: #dc2626;
  font-weight: 600;
  text-align: center;
  font-size: 16px;
  line-height: 1.4;
`;

const ModalButton = styled.button<{ variant?: "danger" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #dc2626;
    color: #ffffff;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

type IFundraiserDetailProps = {
  navigate: NavigateFunction;
};

const FundraiserDetails = observer(({ navigate }: IFundraiserDetailProps) => {
  const { fundraiserDetailsPageData, selectedTab, selectedFundraiserId } =
    fundraiserStore;

  const [searchTerm, setSearchTerm] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSocialShareModal, setShowSocialShareModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteAgreement, setDeleteAgreement] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Auto-scroll to top when Fundraiser Details opens
    window.scrollTo({ top: 0, behavior: "smooth" });

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!selectedFundraiserId) {
      navigate("/fundraiser/dashboard");
      return;
    }
    fetchFundraiserData();
    fetchPromoters();
    fetchDonations();
    fundraiserStore.fetchListInterestTags();
    fundraiserStore.fetchMindMeContactReferences();
    fundraiserStore.fetchMindMeContacts();
    fetchSubscriptions();
  }, [selectedFundraiserId]);

  const fetchFundraiserData = async () => {
    try {
      setShowLoader(true);
      const response = await firstValueFrom(
        FundraiserService.getFundraiserData(selectedFundraiserId || "", ""),
      );
      fundraiserStore.resetBuilder(AuthService.shortUniqueId);
      const data = CreateFundraiserModel.deserializeAPIModel(response.result);
      fundraiserStore.setFundraiserDetails(data);
      fundraiserStore.setFundraiserDetailPageData(data);
    } catch (error) {
      console.error("Failed to load fundraisers", error);
    } finally {
      setShowLoader(false);
    }
  };

  const fetchPromoters = async () => {
    const response = await firstValueFrom(
      FundraiserService.getPromoters(
        AuthService.authData.accountId,
        selectedFundraiserId || "",
        "",
        1,
        99999,
      ),
    );
    if (response.result) {
      const data = response.result.map((p) =>
        PromoterProgressModel.deserializeAPIModel(
          p,
          selectedFundraiserId || "",
        ),
      );
      fundraiserStore.setPromoters(data);
    }
  };

  const fetchDonations = async () => {
    const response = await firstValueFrom(
      FundraiserService.getDonations(
        AuthService.authData.accountId,
        selectedFundraiserId || "",
        "",
        1,
        99999,
      ),
    );
    if (response.result) {
      const data = response.result.map((d) =>
        FundraisersDonationsBoardResponse.deserializeAPIModel(d),
      );
      fundraiserStore.setDonations(data);
    }
  };

  const fetchSubscriptions = async () => {
    const response = await firstValueFrom(
      FundraiserService.getSubscriptions(
        AuthService.authData.accountId,
        selectedFundraiserId || "",
        "",
        1,
        99999,
      ),
    );
    if (response.result) {
      const data = response.result.map((d) =>
        SubscriptionModel.deserializeAPIModel(d),
      );
      fundraiserStore.setSubscriptions(data);
    }
  };

  // Handle escape key for delete modal
  useEffect(() => {
    const handleDeleteModalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showDeleteModal) {
        setShowDeleteModal(false);
        setDeleteConfirmName("");
        setDeleteAgreement(false);
      }
    };

    if (showDeleteModal) {
      document.addEventListener("keydown", handleDeleteModalKeyDown);
      return () => {
        document.removeEventListener("keydown", handleDeleteModalKeyDown);
      };
    }
  }, [showDeleteModal]);

  if (!fundraiserDetailsPageData) {
    return <div>Fundraiser not found</div>;
  }

  const donations = fundraiserStore.getFundraiserDonations(
    fundraiserDetailsPageData.id,
  );

  const subscriptions = fundraiserStore.getFundraiserSubscriptions(
    fundraiserDetailsPageData.id,
  );
  const promoters = fundraiserDetailsPageData.promoterGridData;
  // const promoters = fundraiserStore.getFundraiserPromoters(
  //   fundraiserDetailsPageData.id
  // );
  const hasPayments = fundraiserDetailsPageData.totalRaisedAmount > 0;

  const handlePreview = () => {
    const fundraiserUrl = fundraiserStore.getFundraiserPublicLinkFromModel(
      fundraiserDetailsPageData,
      "",
    );
    window.open(fundraiserUrl, "_blank");
  };

  const handleEdit = () => {
    fundraiserStore.setSelectedFundraiser(fundraiserDetailsPageData.id);
    navigate("/fundraiser/builder");
  };

  const handleUpdateLink = () => {
    setShowLinkModal(true);
  };

  const handleSocialShare = () => {
    setShowSocialShareModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canManageFundraiser("Delete")) return;
    if (selectedFundraiserId) {
      setIsProcessing(true);
      const response = await firstValueFrom(
        FundraiserService.deleteFundraiser(
          AuthService.authData.accountId,
          selectedFundraiserId,
          AuthService.mindmeAccountDetails?.userId || "",
        ),
      );
      if (response.result.success) {
        ToastService.showSuccess("Fundraiser deleted successfully");
        fundraiserStore.setFundraiserDetailPageData(undefined);
        // fundraiserStore.setView("dashboard");
        navigate("/fundraiser/dashboard");
        setShowDeleteModal(false);
      } else {
        ToastService.showError("Failed to delete fundraiser");
      }
    }
    setIsProcessing(false);
  };

  const handleEmbed = () => {
    setShowEmbedModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleInvite = () => {
    fundraiserStore.setSelectedFundraiser(fundraiserDetailsPageData.id);
    navigate(`/fundraiser/promoter-invite`);
  };
  const handleCopyFundraiser = () => {
    setShowCopyModal(true);
  };

  const handleUpdateStatus = () => {
    setShowStatusModal(true);
  };

  const handleStatusUpdate = (refresh?: boolean) => {
    setShowStatusModal(false);
    if (refresh) {
      fetchFundraiserData();
    }
  };

  const handleLinkCopied = (message: string) => {
    // setToastMessage(message);
    // setShowToast(true);
  };

  const handleLinkUpdatedAndCopied = (message: string) => {
    // setToastMessage(message);
    // setShowToast(true);
  };

  const handleCloseLinkPopUp = (refresh: boolean) => {
    setShowLinkModal(false);
    if (refresh) {
      fetchFundraiserData();
    }
  };

  const isDeleteEnabled = hasPayments
    ? deleteConfirmName === fundraiserDetailsPageData?.name && deleteAgreement
    : true;

  if (showLoader) {
    return (
      <DetailsContainer>{new Loader(true).loaderComponent}</DetailsContainer>
    );
  }
  function renderTabContent() {
    switch (selectedTab) {
      case "details":
        return (
          <FundraiserActivityTab
            fundraiser={fundraiserDetailsPageData}
            handleStatusUpdate={handleStatusUpdate}
          />
        );
      case "promoters":
        return (
          <FundraiserPromotersTab
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        );
      case "donors":
        return (
          <FundraiserDonorsTab
            donations={donations}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        );
      case "donations":
        return (
          <FundraiserDonationsTab
            donations={donations}
            subscriptions={subscriptions}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onCloseRefund={(refresh?: boolean) => {
              if (refresh) {
                fetchDonations();
                fetchSubscriptions();
              }
            }}
          />
        );
      default:
        return null;
    }
  }

  return (
    <DetailsContainer>
      <HeaderContainer isScrolled={isScrolled}>
        <Header>
          <LeftSection>
            <BackButton onClick={() => navigate("/fundraiser/dashboard")}>
              <ArrowLeft size={20} />
            </BackButton>
            <Title>Details</Title>
          </LeftSection>

          <CenterSection>
            <TabWrapper>
              <Tab
                active={selectedTab === "details"}
                onClick={() => fundraiserStore.setSelectedTab("details")}
              >
                Activity
              </Tab>
              <Tab
                active={selectedTab === "donations"}
                onClick={() => fundraiserStore.setSelectedTab("donations")}
              >
                Donations
              </Tab>
              <Tab
                active={selectedTab === "donors"}
                onClick={() => fundraiserStore.setSelectedTab("donors")}
              >
                Donors
              </Tab>
              <Tab
                active={selectedTab === "promoters"}
                onClick={() => fundraiserStore.setSelectedTab("promoters")}
              >
                Promoters
              </Tab>
            </TabWrapper>
          </CenterSection>

          <RightSection>
            <EditActionsDropdown
              onEdit={handleEdit}
              onInvite={handleInvite}
              onUpdateLink={handleUpdateLink}
              onShare={handleSocialShare}
              onCopy={handleCopyFundraiser}
              onEmbed={handleEmbed}
              onStatus={handleUpdateStatus}
              onDelete={handleDelete}
            />
            <HeaderButton variant="primary" onClick={handlePreview}>
              <Heart size={16} />
              Donate
            </HeaderButton>
          </RightSection>
        </Header>
      </HeaderContainer>

      <ContentWrapper>
        <ContentCard>{renderTabContent()}</ContentCard>
      </ContentWrapper>

      {fundraiserDetailsPageData && (
        <UpdateLinkModal
          isVisible={showLinkModal}
          onClose={(refresh) => handleCloseLinkPopUp(refresh === true)}
          fundraiser={fundraiserDetailsPageData}
          hasPromoters={promoters?.length > 0}
          onLinkCopied={handleLinkCopied}
          onLinkUpdatedAndCopied={handleLinkUpdatedAndCopied}
        />
      )}

      {/* Social Share Modal */}
      <SocialShareModal
        isVisible={showSocialShareModal}
        gallerySettings={fundraiserStore.gallerySettings}
        onClose={() => setShowSocialShareModal(false)}
        shortUniqueId={AuthService.shortUniqueId}
      />

      <CopyFundraiserModal
        name={fundraiserDetailsPageData?.name || ""}
        fundraiserId={fundraiserDetailsPageData?.id || ""}
        isVisible={showCopyModal}
        onClose={() => setShowCopyModal(false)}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        isVisible={showStatusModal}
        onClose={(refresh?: boolean) => handleStatusUpdate(refresh === true)}
        fundraiser={fundraiserDetailsPageData}
      />

      {/* Embed Fundraiser Modal */}
      <EmbedFundraiserModal
        isVisible={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
      />

      {/* Delete Modal */}
      <Modal
        visible={showDeleteModal}
        onClick={(e: React.MouseEvent) => {
          if (e.target === e.currentTarget) {
            setShowDeleteModal(false);
            setDeleteConfirmName("");
            setDeleteAgreement(false);
          }
        }}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Delete Fundraiser</ModalTitle>
          </ModalHeader>

          <CloseButton
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmName("");
              setDeleteAgreement(false);
            }}
          >
            <X size={20} />
          </CloseButton>

          <ModalText>
            Are you sure you want to delete this fundraiser?
          </ModalText>

          {hasPayments && (
            <>
              <WarningText>
                This fundraiser has received payments. Deleting it will
                permanently remove all donation history and cannot be
                reactivated.
              </WarningText>

              <FormGroup>
                <FormLabel>
                  Type <strong>{fundraiserDetailsPageData?.name}</strong> below
                  exactly as it appears.
                </FormLabel>
                <DeleteConfirmInput
                  hasError={
                    deleteConfirmName.length > 0 &&
                    deleteConfirmName !== fundraiserDetailsPageData?.name
                  }
                  type="text"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder="Enter fundraiser name"
                />
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    checked={deleteAgreement}
                    onChange={(e) => setDeleteAgreement(e.target.checked)}
                  />
                  I understand that this fundraiser will be deleted and cannot
                  be reactivated.
                </CheckboxContainer>
              </FormGroup>
            </>
          )}

          <ModalActions
            style={{
              justifyContent: hasPayments ? "flex-end" : "center",
              marginTop: hasPayments ? "24px" : "32px",
            }}
          >
            <ModalButton
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmName("");
                setDeleteAgreement(false);
              }}
            >
              Cancel
            </ModalButton>
            <ModalButton
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={!isDeleteEnabled || isProcessing}
            >
              Delete Fundraiser
            </ModalButton>
          </ModalActions>
        </ModalContent>
      </Modal>
    </DetailsContainer>
  );
});

export default withRouter(FundraiserDetails);
